import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2,
    LogOut,
    RefreshCw,
    Trash2,
    Mail,
    Phone,
    Home,
    Inbox,
    Lock,
} from "lucide-react";
import { API, LOGO_URL } from "@/config";

const TOKEN_KEY = "fama_admin_token";

const authHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
});

function LoginView({ onLogin }) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/admin/login`, {
                email: email.trim(),
                password,
            });
            localStorage.setItem(TOKEN_KEY, data.token);
            toast.success("Accesso effettuato");
            onLogin();
        } catch (err) {
            toast.error(
                err.response?.data?.detail || "Accesso non riuscito. Riprova.",
            );
        } finally {
            setLoading(false);
        }
    };

    const inputCls =
        "w-full border-0 border-b border-[var(--fama-border)] bg-transparent py-3 font-body text-base text-[var(--fama-ink)] placeholder:text-[var(--fama-ink-soft)]/60 focus:border-[var(--fama-gold)] focus:outline-none focus:ring-0 transition-colors";

    return (
        <div className="flex min-h-screen items-center justify-center bg-[var(--fama-bg)] px-5">
            <div className="w-full max-w-md rounded-[2px] border border-[var(--fama-border)] bg-white p-8 sm:p-10">
                <img src={LOGO_URL} alt="FaMa Apartments" className="mx-auto h-20 w-auto" />
                <div className="mt-6 flex items-center justify-center gap-2 text-[var(--fama-gold)]">
                    <Lock size={16} />
                    <h1 className="font-body text-xs font-semibold uppercase tracking-[0.24em]">
                        Area riservata
                    </h1>
                </div>
                <form onSubmit={submit} data-testid="admin-login-form" className="mt-8 space-y-6">
                    <div>
                        <label className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[var(--fama-ink-soft)]">
                            Email
                        </label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={inputCls}
                            data-testid="admin-email"
                            required
                        />
                    </div>
                    <div>
                        <label className="font-body text-xs font-semibold uppercase tracking-[0.18em] text-[var(--fama-ink-soft)]">
                            Password
                        </label>
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={inputCls}
                            data-testid="admin-password"
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        disabled={loading}
                        data-testid="admin-login-submit"
                        className="flex w-full items-center justify-center gap-2 rounded-full bg-[var(--fama-gold)] px-8 py-4 font-body text-base font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[#b28e1f] disabled:opacity-70"
                    >
                        {loading ? <Loader2 size={18} className="animate-spin" /> : "Accedi"}
                    </button>
                </form>
            </div>
        </div>
    );
}

function DashboardView({ onLogout }) {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/leads`, authHeader());
            setLeads(data);
        } catch (err) {
            if (err.response?.status === 401) {
                toast.error("Sessione scaduta");
                onLogout();
            } else {
                toast.error("Errore nel caricamento delle richieste");
            }
        } finally {
            setLoading(false);
        }
    }, [onLogout]);

    useEffect(() => {
        load();
    }, [load]);

    const remove = async (id) => {
        try {
            await axios.delete(`${API}/admin/leads/${id}`, authHeader());
            setLeads((l) => l.filter((x) => x.id !== id));
            toast.success("Richiesta eliminata");
        } catch {
            toast.error("Impossibile eliminare la richiesta");
        }
    };

    const fmt = (iso) => {
        try {
            return new Date(iso).toLocaleString("it-IT", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return iso;
        }
    };

    return (
        <div className="min-h-screen bg-[var(--fama-bg)]">
            <header className="sticky top-0 z-10 flex items-center justify-between border-b border-[var(--fama-border)] bg-[#fdfbf7]/85 px-5 py-4 backdrop-blur-xl sm:px-8">
                <div className="flex items-center gap-4">
                    <img src={LOGO_URL} alt="FaMa Apartments" className="h-11 w-auto" />
                    <div className="hidden sm:block">
                        <p className="font-serif-display text-xl leading-none text-[var(--fama-ink)]">
                            Richieste ricevute
                        </p>
                        <p className="font-body text-xs text-[var(--fama-ink-soft)]">
                            {leads.length} in totale
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={load}
                        data-testid="admin-refresh"
                        className="flex items-center gap-2 rounded-full border border-[var(--fama-border)] px-4 py-2 font-body text-sm text-[var(--fama-ink)] transition-colors hover:bg-[var(--fama-gold-soft)]"
                    >
                        <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
                        <span className="hidden sm:inline">Aggiorna</span>
                    </button>
                    <button
                        onClick={onLogout}
                        data-testid="admin-logout"
                        className="flex items-center gap-2 rounded-full bg-[var(--fama-ink)] px-4 py-2 font-body text-sm text-white transition-colors hover:bg-black"
                    >
                        <LogOut size={16} />
                        <span className="hidden sm:inline">Esci</span>
                    </button>
                </div>
            </header>

            <main className="mx-auto max-w-6xl px-5 py-8 sm:px-8" data-testid="admin-dashboard">
                {loading ? (
                    <div className="flex justify-center py-24 text-[var(--fama-gold)]">
                        <Loader2 size={32} className="animate-spin" />
                    </div>
                ) : leads.length === 0 ? (
                    <div className="flex flex-col items-center justify-center rounded-[2px] border border-dashed border-[var(--fama-border)] py-24 text-center">
                        <Inbox size={40} className="text-[var(--fama-gold)]/50" />
                        <p className="mt-4 font-serif-display text-2xl text-[var(--fama-ink)]">
                            Nessuna richiesta
                        </p>
                        <p className="mt-1 font-body text-sm text-[var(--fama-ink-soft)]">
                            Le richieste dal form compariranno qui.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {leads.map((lead) => (
                            <article
                                key={lead.id}
                                data-testid={`lead-${lead.id}`}
                                className="rounded-[2px] border border-[var(--fama-border)] bg-white p-6"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <h3 className="font-serif-display text-2xl leading-tight text-[var(--fama-ink)]">
                                            {lead.name}
                                        </h3>
                                        <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2 font-body text-sm text-[var(--fama-ink-soft)]">
                                            <a
                                                href={`mailto:${lead.email}`}
                                                className="flex items-center gap-2 hover:text-[var(--fama-ink)]"
                                            >
                                                <Mail size={15} className="text-[var(--fama-gold)]" />
                                                {lead.email}
                                            </a>
                                            {lead.phone && (
                                                <span className="flex items-center gap-2">
                                                    <Phone size={15} className="text-[var(--fama-gold)]" />
                                                    {lead.phone}
                                                </span>
                                            )}
                                            {lead.property_type && (
                                                <span className="flex items-center gap-2">
                                                    <Home size={15} className="text-[var(--fama-gold)]" />
                                                    {lead.property_type}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="font-body text-xs text-[var(--fama-ink-soft)]">
                                            {fmt(lead.created_at)}
                                        </span>
                                        <button
                                            onClick={() => remove(lead.id)}
                                            data-testid={`lead-delete-${lead.id}`}
                                            aria-label="Elimina richiesta"
                                            className="text-[var(--fama-ink-soft)] transition-colors hover:text-red-600"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                </div>
                                {lead.message && (
                                    <p className="mt-4 border-t border-[var(--fama-border)] pt-4 font-body text-sm leading-relaxed text-[var(--fama-ink)]">
                                        {lead.message}
                                    </p>
                                )}
                            </article>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}

export default function Admin() {
    const [authed, setAuthed] = useState(!!localStorage.getItem(TOKEN_KEY));

    const logout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setAuthed(false);
    };

    return authed ? (
        <DashboardView onLogout={logout} />
    ) : (
        <LoginView onLogin={() => setAuthed(true)} />
    );
}
